import { useEffect } from 'react';
import { dentroApp } from '../utils/dentroApp';
import { useTranslation } from 'react-i18next';
import { usePricing } from '../hooks/usePricing';
import { useCurrency } from '../hooks/useCurrency';
import { useStartCheckout2TB } from '../hooks/useStartCheckout2TB';
import InAppBrowserWarning from '../components/InAppBrowserWarning';
import { apiLogPostRegisterEvent } from '../utils/api';

// Shown exactly once, right after a free registration, before the vault
// ever mounts (see App.js's page==='post-register' branch and AuthPage's
// onRegistered). Replaces silently landing in an empty file manager while
// still believing the account came with 2TB — the reason, per the
// 2026-08-28 funnel numbers, only ~5% of registrations ever paid.
//
// Deliberately no "today only" language and no struck-through prices —
// the deadline below is the one real date (founderOffer.js), stated once,
// in ONE string (see postRegister.json's `deadline` key — swap that one
// line Monday, nothing else). Both buttons are the same size/weight on
// purpose: a hidden or guilt-tripped free option is a dark pattern, not
// just bad UX, and the kind of thing that gets ad accounts suspended.
export default function PostRegisterUpsellPage({ campaign, onContinueFree }) {
  // DENTRO L'APP QUESTA PAGINA NON SI MOSTRA (2026-09-13).
  //
  // E' una pagina d'acquisto: il suo pulsante porta dritto a Stripe. Dentro
  // un'app un percorso che porta a pagare fuori dal sistema di Google non e'
  // ammesso, ed e' la condizione per pubblicare. Chi si registra dall'app va
  // direttamente al vault, che e' quello che voleva fare.
  //
  // Il salto avviene qui e non in App.js perche' e' qui che vive il motivo:
  // chi legge questo file deve trovare la ragione accanto alla riga.
  // onContinueFree nelle dipendenze: e' stabile, e se un domani non lo fosse
  // l'effetto scatterebbe due volte su una pagina che comunque non si vede.
  useEffect(() => {
    if (dentroApp()) onContinueFree();
  }, [onContinueFree]);
  if (dentroApp()) return null;

  const { t } = useTranslation('postRegister');
  const { currency } = useCurrency();
  const { priceOf } = usePricing();
  const price2tb = priceOf('lifetime_2tb', currency);
  // 2026-09-01 — full price here too. The welcome discount is private to
  // the file manager now, and this screen is not it.
  const priceAmount = price2tb?.amount;
  const priceSymbol = price2tb?.symbol;

  const { buy2tb, loading: upgradeLoading, error: upgradeError, inAppWarningProps } = useStartCheckout2TB(campaign);

  useEffect(() => {
    apiLogPostRegisterEvent('shown', 'lifetime_2tb', campaign).catch(() => {});
    // eslint-disable-next-line
  }, []);

  function handleUpgrade() {
    apiLogPostRegisterEvent('clicked_upgrade', 'lifetime_2tb', campaign).catch(() => {});
    buy2tb();
  }

  function handleFree() {
    apiLogPostRegisterEvent('clicked_free', null, campaign).catch(() => {});
    onContinueFree();
  }

  return (
    <div className="pru-page">
      <div className="pru-card">
        <div className="pru-icon">🆓</div>
        <h1 className="pru-title">{t('title')}</h1>
        <p className="pru-explainer">{t('explainer')}</p>
        <div className="pru-divider" />
        <p className="pru-pitch">{t('pitch')}</p>

        {upgradeError && <div className="auth-error">⚠ {upgradeError}</div>}

        <div className="pru-actions">
          <button type="button" className="pru-btn-primary" onClick={handleUpgrade} disabled={upgradeLoading}>
            {priceAmount != null ? t('upgrade_cta', { symbol: priceSymbol, price: priceAmount }) : t('upgrade_cta_fallback')}
          </button>
          <button type="button" className="pru-btn-secondary" onClick={handleFree} disabled={upgradeLoading}>
            {t('free_cta')}
          </button>
        </div>
      </div>

      {inAppWarningProps && <InAppBrowserWarning {...inAppWarningProps} />}
    </div>
  );
}
